import { isPaymentAccount } from '@outss/ledger-core';
import { featureOfKind } from '@outss/ledger-core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Link, useBlocker, useNavigate } from '@tanstack/react-router';
import { ChevronDown, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { AmountInput } from '@/components/amount-input';
import { Combobox, type ComboItem } from '@/components/combobox';
import { ExchangeRateField, HomeTotals, useEffectiveRate } from '@/components/currency';
import { DateInput } from '@/components/date-input';
import { ErrorBanner, Field, NativeSelect, Textarea } from '@/components/form';
import { EInvoiceChip } from '@/components/einvoicing/einvoice-chip';
import { EInvoicePanel } from '@/components/einvoicing/einvoice-panel';
import { ApprovalStrip } from '@/components/portal/approval-strip';
import { AttachmentsPanel } from '@/components/portal/attachments-panel';
import { EwayBillPanel } from '@/components/gst/eway-bill-panel';
import { IrnPanel } from '@/components/gst/irn-panel';
import { ApplyCreditDialog } from '@/components/sales/apply-credit-dialog';
import { DocumentActivity } from '@/components/sales/document-activity';
import { SendDialog } from '@/components/sales/send-dialog';
import { ClassSelect, TrackingHeaderFields } from '@/components/tracking/tracking-fields';
import { StatusBadge } from '@/components/sales/status-badge';
import { VoidDialog } from '@/components/sales/void-dialog';
import { OverlayPage } from '@/components/overlay-page';
import { toast } from '@/components/toast';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { formatRate, taxCodesFor, useAccounts, useTaxCodes } from '@/lib/accounts';
import { ApiError, api } from '@/lib/api';
import { formatTrn, useMe } from '@/lib/clients';
import { isLocked, useEInvoice } from '@/lib/einvoicing';
import { formatAddress, useContacts, type Contact } from '@/lib/contacts';
import { exponentOf, fromHomePrice, toHomeMinor, useHomeCurrency } from '@/lib/currencies';
import { todayIso } from '@/lib/dates';
import { pdfAction, pdfFileName, type PdfMode } from '@/lib/pdf';
import { formatAmount, formatDate } from '@/lib/format';
import { useHotkeys } from '@/lib/hotkeys';
import { nameFor } from '@/lib/arabic';
import { i18n, useLanguage, useT } from '@/lib/i18n';
import { useItems } from '@/lib/items';
import { formatMinor, minorToPlain, toMinor } from '@/lib/money';
import { openNewDrawer } from '@/lib/new-drawer';
import { useOrgId } from '@/lib/org';
import {
  EMPTY_TRACKING,
  trackingBody,
  trackingFrom,
  useTrackingSettings,
  type TrackingValue,
} from '@/lib/tracking';
import { setDocumentTemplateSeed } from '@/lib/recurring-documents';
import { documentProblems, mapLineErrors, normalizeQuantity, rowUsed } from '@/lib/sales-form';
import {
  clientCanEdit,
  formReadOnly,
  useDocumentApprovals,
  useOrgRole,
  postingOf,
  useFeatureSet,
} from '@/lib/portal';
import {
  CHALLAN_TYPES,
  KIND_INFO,
  ORDER_KINDS,
  PAYMENT_METHODS,
  lineAmounts,
  parseQuantity,
  challanTypeLabel,
  kindDateLabel,
  kindLabel,
  kindNumberLabel,
  ltrIn,
  paymentMethodLabel,
  rateFor,
  useSalesDocuments,
  type AmountsAre,
  type ChallanType,
  type DocumentLine,
  type SalesDocument,
  type SalesKind,
} from '@/lib/sales';
import { EMIRATES, TERMS, emirateLabel, useCompanySettings } from '@/lib/settings';
import { cn } from '@/lib/utils';
import { GST_STATES, useEInvoicePreview, useEwayBill } from '@/lib/gst';
import { isIndia, orgLocale } from '@/lib/org-locale';

interface Row {
  key: number;
  itemId: string | null;
  description: string;
  quantity: string;
  rate: string;
  taxCodeId: string | null;
  accountId: string | null;
  /** India: HSN or SAC code (from the product/service). */
  hsnSac: string;
  /** Class of the row when classes are tracked per row. */
  classId: string | null;
  /** The sales order and challan lines this row fulfils (document 06 S14). */
  soLineId: string | null;
  dcLineId: string | null;
}

let seq = 0;
const emptyRow = (taxCodeId: string | null = null): Row => ({
  key: ++seq,
  itemId: null,
  description: '',
  quantity: '',
  rate: '',
  taxCodeId,
  accountId: null,
  hsnSac: '',
  classId: null,
  soLineId: null,
  dcLineId: null,
});
const used = (r: Row) => rowUsed(r);

const POSTED = ['approved', 'partially_paid', 'paid'];
/** Never posted and numbered when saved: quotes, sales orders, delivery challans. */
const UNPOSTED: readonly SalesKind[] = ['quote', ...ORDER_KINDS];

/** "100" − "60" → "40" (quantities up to 6 decimals, no floats); null when nothing is left. */
export function remainingQuantity(quantity: string, done: string | undefined): string | null {
  const q = parseQuantity(quantity);
  const d = parseQuantity(done ?? '0') ?? { digits: 0n, scale: 0 };
  if (!q) return null;
  const scale = Math.max(q.scale, d.scale);
  const left =
    q.digits * 10n ** BigInt(scale - q.scale) - d.digits * 10n ** BigInt(scale - d.scale);
  if (left <= 0n) return null;
  const t = left.toString().padStart(scale + 1, '0');
  const out = scale ? `${t.slice(0, -scale)}.${t.slice(-scale)}` : t;
  return out.includes('.') ? out.replace(/0+$/, '').replace(/\.$/, '') : out;
}

/** What is still to do on a line of a sales order or challan, for the document made from it. */
function openPart(target: SalesKind, from: SalesDocument, l: DocumentLine): string | null {
  if (l.closed) return null;
  const done =
    target === 'delivery_challan' && from.kind === 'sales_order'
      ? l.deliveredQuantity
      : l.invoicedQuantity;
  return remainingQuantity(l.quantity, done);
}

interface FormState {
  contactId: string | null;
  customerEmail: string;
  termsDays: number | null;
  issueDate: string;
  dueDate: string;
  dueEdited: boolean;
  supplyDate: string;
  placeOfSupply: string;
  reference: string;
  amountsAre: AmountsAre;
  memo: string;
  statementMemo: string;
  creditReason: string;
  relatedDocumentId: string | null;
  depositAccountId: string | null;
  paymentMethod: string;
  /** Exchange rate typed on a foreign-currency document; '' = the day's rate from Currencies. */
  fxRate: string;
  /** Location, class, project and tags (document 06 L4). */
  tracking: TrackingValue;
  /** Delivery challans: why the goods move (rule 55). */
  challanType: ChallanType | '';
  rows: Row[];
}

const addDays = (iso: string, n: number) => {
  const [y, m, d] = iso.split('-').map(Number) as [number, number, number];
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
};

function initialState(
  kind: SalesKind,
  doc: SalesDocument | null,
  source: SalesDocument | null,
  defaults: { memo: string | null; contactId: string | null },
  fromOrder = false,
): FormState {
  const from = doc ?? source;
  const row = (l: DocumentLine, quantity: string, links: Partial<Row> = {}): Row => ({
    key: ++seq,
    itemId: l.itemId,
    description: l.description ?? '',
    quantity,
    rate: l.unitPrice,
    taxCodeId: l.taxCodeId,
    accountId: l.accountId,
    hsnSac: l.hsnSac ?? '',
    classId: l.classId ?? null,
    soLineId: null,
    dcLineId: null,
    ...links,
  });
  const rows: Row[] = doc
    ? doc.lines.map((l) =>
        row(l, l.quantity, { soLineId: l.soLineId ?? null, dcLineId: l.dcLineId ?? null }),
      )
    : source && fromOrder
      ? // the open part of each line of the sales order or challan, linked to it (S14)
        source.lines.flatMap((l) => {
          const qty = openPart(kind, source, l);
          if (!qty) return [];
          return [
            row(
              l,
              qty,
              source.kind === 'sales_order'
                ? { soLineId: l.id ?? null }
                : { dcLineId: l.id ?? null, soLineId: l.soLineId ?? null },
            ),
          ];
        })
      : source
        ? source.lines.map((l) => row(l, l.quantity))
        : [];
  while (rows.length < 2) rows.push(emptyRow());
  const today = todayIso();
  return {
    contactId: from?.contactId ?? defaults.contactId,
    customerEmail: from?.customerEmail ?? '',
    termsDays: doc?.termsDays ?? source?.termsDays ?? null,
    issueDate: doc?.issueDate ?? today,
    dueDate: doc?.dueDate ?? '',
    dueEdited: Boolean(doc?.dueDate),
    supplyDate: doc?.supplyDate ?? '',
    placeOfSupply: from?.placeOfSupply ?? '',
    reference: from?.reference ?? '',
    amountsAre: from?.amountsAre ?? 'exclusive',
    memo: doc ? (doc.memo ?? '') : (source?.memo ?? defaults.memo ?? ''),
    statementMemo: doc?.statementMemo ?? '',
    creditReason: doc?.creditReason ?? '',
    relatedDocumentId: doc?.relatedDocumentId ?? null,
    depositAccountId: doc?.depositAccountId ?? null,
    paymentMethod: doc?.paymentMethod ?? '',
    // a saved foreign document keeps its rate unless it is cleared
    fxRate: doc && doc.currency !== doc.homeCurrency ? doc.fxRate : '',
    // a copy keeps the dimensions but not the tags of its source
    tracking: doc
      ? trackingFrom(doc)
      : source
        ? { ...trackingFrom(source), tagIds: [] }
        : EMPTY_TRACKING,
    challanType: doc?.challanType ?? (source?.kind === kind ? (source.challanType ?? '') : ''),
    rows,
  };
}

/**
 * Transaction form (document 05 pattern 3; document 06 S4, S5, S6, S8, S13–S15): invoice, quote,
 * credit note, sales receipt, sales order or delivery challan. New and draft documents save as
 * drafts or approve (which numbers and posts them); approved ones save as a repost; void ones are
 * read-only. Quotes, sales orders and challans are numbered when saved and never posted.
 */
export function DocumentForm({
  kind,
  doc,
  source,
  quoteId,
  fromOrder,
  defaultContactId,
  openApply,
  openSend,
}: {
  openApply?: boolean;
  openSend?: boolean;
  kind: SalesKind;
  doc: SalesDocument | null;
  /** Prefill (copy, quote conversion, or the open lines of a sales order or challan). */
  source?: SalesDocument | null;
  quoteId?: string;
  /** `source` is a sales order or challan this document fulfils (its lines stay linked). */
  fromOrder?: boolean;
  defaultContactId?: string;
}) {
  const t = useT();
  const lang = useLanguage();
  const info = KIND_INFO[kind];
  const label = kindLabel(kind);
  // the tax's name in sentences, and its short form for column heads and line labels
  const taxName = t(isIndia() ? 'purchasePages.taxWord.gst' : 'purchasePages.taxWord.vat');
  const taxShort = t(
    isIndia() ? 'purchasePages.taxWord.gstShort' : 'purchasePages.taxWord.vatShort',
  );
  const orgId = useOrgId()!;
  const navigate = useNavigate();
  const qc = useQueryClient();
  const me = useMe();
  const settings = useCompanySettings();
  const contacts = useContacts({ kind: 'customer' });
  const items = useItems();
  const taxCodes = useTaxCodes({ includeInactive: true });
  const accounts = useAccounts();
  const homeFallback = useHomeCurrency();
  const isStaff = me.data?.isStaff ?? false;
  // client portal: a client owner drafts invoices for Outss to approve, where switched on
  const role = useOrgRole();
  const features = useFeatureSet();
  const docApprovals = useDocumentApprovals(doc?.status === 'draft' ? doc.id : undefined);
  const posting = postingOf(features, featureOfKind(kind));
  const clientEdit =
    !isStaff &&
    clientCanEdit({
      role,
      posting,
      kind,
      doc,
      approvals: docApprovals.data,
    });
  // under approval posting the owner's save is a draft for the accountant; under direct posting
  // (ADR-0017) the owner saves and posts like staff
  const clientDraft = clientEdit && posting === 'approval';

  const [s, setS] = useState<FormState>(() =>
    initialState(
      kind,
      doc,
      source ?? null,
      {
        memo:
          kind === 'invoice' || kind === 'sales_receipt'
            ? (settings.data?.sales.invoiceMessage ?? null)
            : null,
        contactId: defaultContactId ?? null,
      },
      fromOrder,
    ),
  );
  const initial = useRef(JSON.stringify(s));
  const [errors, setErrors] = useState<string[]>([]);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [voiding, setVoiding] = useState(false);
  const [applying, setApplying] = useState(Boolean(openApply));
  const [sending, setSending] = useState(Boolean(openSend));
  const leaving = useRef(false);

  const isVoid = doc?.status === 'void';
  const posted = Boolean(doc && POSTED.includes(doc.status));
  // e-invoices: a submitted or accepted one is corrected by credit note, never edited (document 07)
  const einvoice = useEInvoice(
    doc && doc.status !== 'draft' && (kind === 'invoice' || kind === 'credit_note')
      ? doc.id
      : undefined,
  );
  const einvoiceRow = einvoice.data?.submission ?? null;
  // India: an active IRN or e-way bill fixes a posted invoice or credit note; the database refuses
  // a repost or void (rules AG, EWB), so the form says so up front instead of failing on save
  const india = isIndia();
  const gstIrn = useEInvoicePreview(
    doc?.id,
    india && posted && (kind === 'invoice' || kind === 'credit_note'),
  );
  const gstEwb = useEwayBill(doc?.id, india && posted && kind === 'invoice');
  const irnActive = Boolean(gstIrn.data?.irn);
  const ewbActive = gstEwb.data?.ewayBill?.status === 'active';
  const gstLocked = posted && (irnActive || ewbActive);
  const einvoiceLocked = isLocked(einvoiceRow) || gstLocked;
  const isOrder = ORDER_KINDS.includes(kind);
  // an order or challan with invoiced or delivered lines is fixed: close it instead (S13)
  const fulfilled = Boolean(
    doc?.lines.some(
      (l) => Number(l.invoicedQuantity ?? 0) > 0 || Number(l.deliveredQuantity ?? 0) > 0,
    ),
  );
  const orderLocked = Boolean(doc && isOrder && (doc.status !== 'open' || fulfilled));
  const readOnly =
    formReadOnly({ isStaff, clientEdit, doc: doc ?? null, einvoiceLocked }) || orderLocked;
  const dirty = JSON.stringify(s) !== initial.current;

  useBlocker({
    shouldBlockFn: () =>
      dirty && !leaving.current && !window.confirm(t('purchasePages.form.unsaved')),
    enableBeforeUnload: () => dirty && !leaving.current,
  });

  const set = <K extends keyof FormState>(k: K, v: FormState[K]) => setS((p) => ({ ...p, [k]: v }));
  const tracking = useTrackingSettings();
  const contact: Contact | undefined = contacts.data?.find((c) => c.id === s.contactId);
  // multi-currency: a document is in its customer's currency (document 06 S4)
  const home = doc?.homeCurrency ?? homeFallback;
  const currency = contact?.currency ?? doc?.currency ?? home;
  const foreign = currency !== home;
  const exp = exponentOf(currency);
  const fxRate = useEffectiveRate(currency, home, s.issueDate, s.fxRate, doc?.fxRate);
  const toHome = (m: bigint) => (fxRate ? toHomeMinor(m, fxRate, exp, exponentOf(home)) : null);
  const salesCodes = useMemo(() => taxCodesFor(taxCodes.data, 'sales'), [taxCodes.data]);
  const codeById = useMemo(
    () => new Map((taxCodes.data ?? []).map((t) => [t.id, t])),
    [taxCodes.data],
  );
  const defaultCode = (c?: Contact) =>
    c?.defaultTaxCodeId ?? salesCodes.find((t) => t.code === 'SR')?.id ?? salesCodes[0]?.id ?? null;

  // ---- totals (bigint fils, mirrors the server) ----
  const totals = useMemo(() => {
    let net = 0n;
    let tax = 0n;
    const byCode = new Map<string, { label: string; tax: bigint }>();
    const perRow = new Map<number, bigint>();
    for (const r of s.rows) {
      if (!used(r)) continue;
      const code = r.taxCodeId ? codeById.get(r.taxCodeId) : undefined;
      const bp = code ? Math.round(Number(code.rate) * 100) : 0;
      const a = lineAmounts(r.quantity || '1', toMinor(r.rate, exp) ?? 0n, bp, s.amountsAre);
      perRow.set(r.key, s.amountsAre === 'inclusive' ? a.gross : a.net);
      net += a.net;
      tax += a.tax;
      if (code && a.tax > 0n) {
        const k = `${code.code} ${formatRate(code.rate)}`;
        byCode.set(k, {
          label: isIndia()
            ? nameFor(lang, code.name, code.nameAr)
            : t('purchasePages.form.vatLine', { rate: formatRate(code.rate), code: code.code }),
          tax: (byCode.get(k)?.tax ?? 0n) + a.tax,
        });
      }
    }
    return { net, tax, total: net + tax, byCode: [...byCode.values()], perRow };
  }, [s.rows, s.amountsAre, codeById, exp, lang, t]);
  const allocated = doc ? (toMinor(doc.total, exp) ?? 0n) - (toMinor(doc.balance, exp) ?? 0n) : 0n;
  const balanceDue =
    kind === 'sales_receipt' ? 0n : posted ? totals.total - allocated : totals.total;

  // ---- pickers ----
  const contactItems: ComboItem[] = useMemo(
    () =>
      (contacts.data ?? []).map((c) => ({
        id: c.id,
        label: nameFor(lang, c.displayName, c.displayNameAr),
        keywords: `${c.displayName} ${c.displayNameAr ?? ''}`,
        hint: (c.companyName && nameFor(lang, c.companyName, c.companyNameAr)) || undefined,
      })),
    [contacts.data, lang],
  );
  const itemItems: ComboItem[] = useMemo(
    () =>
      (items.data ?? [])
        .filter((i) => i.isSold)
        .map((i) => ({
          id: i.id,
          label: nameFor(lang, i.name, i.nameAr),
          keywords: `${i.sku ?? ''} ${i.name} ${i.nameAr ?? ''}`,
          hint: i.salesPrice ? formatAmount(i.salesPrice) : undefined,
        })),
    [items.data, lang],
  );
  const depositAccounts = (accounts.data ?? []).filter(
    (a) => !a.isControl && isPaymentAccount(a.detailType),
  );
  useEffect(() => {
    if (kind === 'sales_receipt' && !doc && !s.depositAccountId && depositAccounts.length)
      setS((p) => ({
        ...p,
        depositAccountId: (
          depositAccounts.find((a) => a.detailType === 'bank') ?? depositAccounts[0]!
        ).id,
      }));
  }, [kind, doc, s.depositAccountId, depositAccounts]);
  const invoicesForCredit = useSalesDocuments(
    'invoice',
    kind === 'credit_note' ? (s.contactId ?? undefined) : undefined,
  );

  const chooseContact = (id: string | null) =>
    setS((p) => {
      const c = contacts.data?.find((x) => x.id === id);
      const terms = c ? c.paymentTermsDays : p.termsDays;
      return {
        ...p,
        contactId: id,
        customerEmail: c?.email ?? '',
        // another customer's currency needs its own rate
        fxRate: c && c.currency === (contact?.currency ?? currency) ? p.fxRate : '',
        termsDays: terms,
        dueDate:
          kind === 'invoice' && !p.dueEdited && terms !== null
            ? addDays(p.issueDate, terms)
            : p.dueDate,
        placeOfSupply: c
          ? isIndia()
            ? c.country === 'IN'
              ? (c.stateCode ?? orgLocale().stateCode ?? '')
              : '96'
            : c.country === 'AE'
              ? (c.emirate ?? settings.data?.company.emirate ?? '')
              : ''
          : p.placeOfSupply,
        relatedDocumentId: null,
        rows: p.rows.map((r) => (used(r) || r.taxCodeId ? r : { ...r, taxCodeId: null })),
      };
    });

  const updateRow = (key: number, patch: Partial<Row>) =>
    setS((p) => ({ ...p, rows: p.rows.map((r) => (r.key === key ? { ...r, ...patch } : r)) }));

  const chooseItem = (key: number, itemId: string | null) => {
    const it = items.data?.find((i) => i.id === itemId);
    updateRow(key, {
      itemId,
      ...(it
        ? {
            description: it.salesDescription ?? it.name,
            rate:
              it.salesPrice && currency !== home && fxRate
                ? fromHomePrice(it.salesPrice, fxRate, exponentOf(currency))
                : (it.salesPrice ?? ''),
            quantity: '1',
            taxCodeId: it.salesTaxCodeId ?? defaultCode(contact),
            accountId: it.salesAccountId,
            hsnSac: it.hsnSac ?? '',
          }
        : {}),
    });
  };

  // ---- save ----
  type Action = 'draft' | 'approve-close' | 'approve-new' | 'approve-send' | 'save';
  const body = (approve: boolean) => ({
    contactId: s.contactId,
    issueDate: s.issueDate,
    ...(kind === 'invoice' ? { termsDays: s.termsDays, dueDate: s.dueDate || null } : {}),
    ...(kind === 'quote' || kind === 'sales_order' ? { dueDate: s.dueDate || null } : {}),
    ...(kind === 'delivery_challan' ? { challanType: s.challanType || null } : {}),
    supplyDate: s.supplyDate || null,
    placeOfSupply: s.placeOfSupply || null,
    amountsAre: s.amountsAre,
    reference: s.reference,
    memo: s.memo,
    statementMemo: s.statementMemo,
    customerEmail: s.customerEmail,
    ...(kind === 'credit_note'
      ? { creditReason: s.creditReason, relatedDocumentId: s.relatedDocumentId }
      : {}),
    ...(kind === 'sales_receipt'
      ? { depositAccountId: s.depositAccountId, paymentMethod: s.paymentMethod || null }
      : {}),
    ...(foreign && s.fxRate ? { fxRate: s.fxRate } : {}),
    ...trackingBody(s.tracking, tracking),
    lines: s.rows.filter(used).map((r) => ({
      ...(tracking.classes === 'per_line' ? { classId: r.classId } : {}),
      itemId: r.itemId,
      accountId: r.accountId,
      description: r.description,
      // ".5" → "0.5", "2." → "2": what the API regex accepts (junk is caught in submit)
      quantity: normalizeQuantity(r.quantity) ?? r.quantity,
      unitPrice: r.rate || '0',
      taxCodeId: r.taxCodeId,
      ...(isIndia() ? { hsnSac: r.hsnSac } : {}),
      ...(r.soLineId ? { soLineId: r.soLineId } : {}),
      ...(r.dcLineId ? { dcLineId: r.dcLineId } : {}),
    })),
    approve,
  });

  /** "Make recurring" (QBO): carry the form over to a new recurring template. */
  const makeRecurring = () => {
    if (kind === 'invoice') {
      setDocumentTemplateSeed({ kind, form: body(false) });
      leaving.current = true;
      void navigate({ to: '/accounting/recurring/new', search: { kind } });
    }
  };

  const save = useMutation({
    mutationFn: async (action: Action) => {
      const approve =
        action === 'approve-close' || action === 'approve-new' || action === 'approve-send';
      if (doc)
        return api<SalesDocument>(`/orgs/${orgId}/sales/documents/${doc.id}`, {
          method: 'PUT',
          body: JSON.stringify({ ...body(approve), version: doc.version }),
        });
      return api<SalesDocument>(`/orgs/${orgId}/sales/documents`, {
        method: 'POST',
        body: JSON.stringify({ kind, ...body(approve), ...(quoteId ? { quoteId } : {}) }),
      });
    },
    onSuccess: async (saved, action) => {
      await qc.invalidateQueries();
      const what = { doc: `${label}${saved.number ? ` ${ltrIn(saved.number)}` : ''}` };
      toast(
        clientDraft
          ? t('purchasePages.form.sentForApproval', what)
          : saved.status === 'draft'
            ? t('purchasePages.form.savedDraft', what)
            : action === 'save' && posted
              ? t('purchasePages.form.reposted', what)
              : UNPOSTED.includes(kind)
                ? t('purchasePages.form.saved', what)
                : t('purchasePages.form.approvedPosted', what),
        { link: { to: `${info.path}/${saved.id}`, label: t('purchasePages.form.view') } },
      );
      for (const w of saved.warnings) toast(w, { tone: 'error' });
      leaving.current = true;
      if (action === 'approve-send') {
        void navigate({ to: `${info.path}/${saved.id}`, search: { send: '1' }, replace: true });
      } else if (action === 'approve-new') {
        void navigate({ to: `${info.path}/new`, search: {}, replace: Boolean(doc) });
        if (!doc) {
          const fresh = initialState(kind, null, null, {
            memo: settings.data?.sales.invoiceMessage ?? null,
            contactId: null,
          });
          initial.current = JSON.stringify(fresh);
          setS(fresh);
          leaving.current = false;
        }
      } else if (action === 'draft' || action === 'save') {
        void navigate({ to: info.listPath });
      } else {
        void navigate({ to: info.listPath });
      }
      setErrors([]);
      setFieldErrors({});
    },
    onError: (e) => {
      if (e instanceof ApiError) {
        // the API numbers lines as sent (blank rows dropped): map them to the rows on screen
        const details = mapLineErrors(
          Array.isArray(e.details) ? (e.details as Array<{ path: string; message: string }>) : [],
          s.rows,
        );
        setFieldErrors(Object.fromEntries(details.map((d) => [d.path, d.message])));
        setErrors(details.length ? details.map((d) => capital(d.message)) : [capital(e.message)]);
      } else setErrors([String(e)]);
    },
  });

  const submit = (action: Action) => {
    if (save.isPending || readOnly) return;
    const hasDue = kind === 'invoice' || kind === 'quote' || kind === 'sales_order';
    const problems = documentProblems({
      contactId: s.contactId,
      issueDate: s.issueDate,
      dueDate: hasDue ? s.dueDate : '',
      dueLabel:
        kind === 'quote'
          ? t('salesPages.documentForm.expirationDateLower')
          : kind === 'sales_order'
            ? t('salesPages.documentForm.shipmentDateLower')
            : t('salesPages.document.dueDateLower'),
      amountsAre: s.amountsAre,
      rows: s.rows,
      taxWord: taxName,
    });
    if (problems.length) {
      setErrors(problems);
      return;
    }
    save.mutate(action);
  };

  const voidM = useMutation({
    mutationFn: ({ date, reason }: { date: string; reason: string }) =>
      api<SalesDocument>(`/orgs/${orgId}/sales/documents/${doc!.id}/void`, {
        method: 'POST',
        body: JSON.stringify({ date, reason }),
      }),
    onSuccess: async (d) => {
      await qc.invalidateQueries();
      const doc = { doc: `${label} ${ltrIn(d.number ?? '')}` };
      toast(
        kind === 'delivery_challan'
          ? t('salesPages.documentForm.cancelled', doc)
          : t('purchasePages.form.voided', doc),
      );
      setVoiding(false);
      leaving.current = true;
      void navigate({ to: `${info.path}/${d.id}`, replace: true });
    },
  });
  const del = useMutation({
    mutationFn: () => api<void>(`/orgs/${orgId}/sales/documents/${doc!.id}`, { method: 'DELETE' }),
    onSuccess: async () => {
      leaving.current = true;
      qc.removeQueries({ queryKey: ['sales', orgId, 'document', doc!.id] });
      await navigate({ to: info.listPath });
      toast(t('purchasePages.form.deleted', { doc: label }));
      await qc.invalidateQueries({ queryKey: ['sales', orgId], refetchType: 'active' });
    },
    onError: (e) => setErrors([e instanceof ApiError ? capital(e.message) : String(e)]),
  });

  const quoteStatus = useMutation({
    mutationFn: (status: 'pending' | 'accepted' | 'declined') =>
      api<SalesDocument>(`/orgs/${orgId}/sales/documents/${doc!.id}/quote-status`, {
        method: 'POST',
        body: JSON.stringify({ status }),
      }),
    onSuccess: async (d) => {
      await qc.invalidateQueries();
      const st = d.status as 'pending' | 'accepted' | 'declined';
      toast(t(`salesPages.documentForm.quoteMarked.${st}`, { number: ltrIn(d.number ?? '') }));
    },
  });
  const canApply =
    kind === 'credit_note' &&
    isStaff &&
    (doc?.status === 'approved' || doc?.status === 'partially_paid');
  const canConvert =
    kind === 'quote' && isStaff && (doc?.status === 'pending' || doc?.status === 'accepted');
  const openOrder = isOrder && isStaff && doc?.status === 'open';
  const closeOrder = useMutation({
    mutationFn: (closed: boolean) =>
      api<SalesDocument>(`/orgs/${orgId}/sales/documents/${doc!.id}/close`, {
        method: 'POST',
        body: JSON.stringify({ closed }),
      }),
    onSuccess: async (d) => {
      await qc.invalidateQueries();
      const what = { doc: `${label} ${ltrIn(d.number ?? '')}` };
      toast(
        d.status === 'closed'
          ? t('salesPages.documentForm.closed', what)
          : t('salesPages.documentForm.reopened', what),
      );
    },
    onError: (e) => setErrors([e instanceof ApiError ? capital(e.message) : String(e)]),
  });

  const canSend =
    isStaff &&
    Boolean(doc) &&
    doc!.status !== 'draft' &&
    doc!.status !== 'void' &&
    doc!.status !== 'converted';
  const pdf = (mode: PdfMode) =>
    void pdfAction(
      mode,
      `/orgs/${orgId}/sales/documents/${doc!.id}/pdf${mode === 'download' ? '?download=1' : ''}`,
      pdfFileName(info.label, doc!.number),
    );

  const close = () => void navigate({ to: info.listPath });
  const primary: Action = clientDraft
    ? 'draft'
    : posted
      ? 'save'
      : kind === 'quote'
        ? 'approve-close'
        : 'approve-close';
  useHotkeys({
    'ctrl+alt+s': () => submit(primary),
    'ctrl+alt+d': () => submit(clientDraft ? 'draft' : posted ? 'save' : 'approve-new'),
    'ctrl+alt+x': close,
    'ctrl+alt+m': () =>
      canSend ? setSending(true) : !posted && !UNPOSTED.includes(kind) && submit('approve-send'),
  });

  const fe = (path: string) => fieldErrors[path];
  const lineErr = (i: number, f: string) => Boolean(fieldErrors[`lines.${i}.${f}`]);
  const title = doc?.number ? `${label} ${ltrIn(doc.number)}` : label;
  const termsOptions =
    TERMS.some((t) => t.days === s.termsDays) || s.termsDays === null
      ? TERMS
      : [
          ...TERMS,
          { days: s.termsDays, label: t('purchasePages.form.netDays', { days: s.termsDays }) },
        ];

  return (
    <OverlayPage
      title={title}
      subtitle={
        doc ? (
          <span className="inline-flex items-center gap-2">
            <StatusBadge kind={kind} status={doc.status} overdue={doc.overdue} />
            {einvoiceRow ? (
              <EInvoiceChip
                status={einvoiceRow.status}
                needsAttention={einvoiceRow.needsAttention}
                prefix={t('salesPages.documentForm.einvoicePrefix')}
              />
            ) : null}
          </span>
        ) : undefined
      }
      onClose={close}
      footerLeft={
        <>
          <Button variant="outline" onClick={close}>
            {readOnly ? t('common.close') : t('common.cancel')}
          </Button>
          {!readOnly ? (
            <Button
              variant="ghost"
              onClick={() => {
                setS(JSON.parse(initial.current) as FormState);
                setErrors([]);
                setFieldErrors({});
              }}
            >
              {doc ? t('purchasePages.form.revert') : t('purchasePages.form.clear')}
            </Button>
          ) : null}
        </>
      }
      footerCentre={
        <>
          {canConvert ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" className="gap-1">
                  {t('salesPages.documentForm.convert')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem asChild>
                  <Link to="/sales/invoices/new" search={{ quoteId: doc!.id }}>
                    {t('salesPages.list.convertToInvoice')}
                  </Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/sales/sales-orders/new" search={{ quoteId: doc!.id }}>
                    {t('salesPages.documentForm.convertToSalesOrder')}
                  </Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
          {openOrder ? (
            <Button variant="outline" asChild>
              <Link to="/sales/invoices/new" search={{ fromId: doc!.id }}>
                {t('salesPages.list.createInvoice')}
              </Link>
            </Button>
          ) : null}
          {openOrder && kind === 'sales_order' && isIndia() ? (
            <Button variant="outline" asChild>
              <Link to="/sales/delivery-challans/new" search={{ fromId: doc!.id }}>
                {t('salesPages.documentForm.createDeliveryChallan')}
              </Link>
            </Button>
          ) : null}
          {canApply ? (
            <Button variant="outline" onClick={() => setApplying(true)}>
              {t('salesPages.documentForm.applyToInvoices')}
            </Button>
          ) : null}
          {doc ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1">
                  {t('salesPages.statementNew.printPreview')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                <DropdownMenuItem onSelect={() => pdf('print')}>
                  {t('salesPages.list.print')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => pdf('preview')}>
                  {t('salesPages.documentForm.previewPdf')}
                </DropdownMenuItem>
                <DropdownMenuItem onSelect={() => pdf('download')}>
                  {t('salesPages.documentForm.downloadPdf')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <span title={t('salesPages.documentForm.saveFirst')}>
              <Button variant="ghost" disabled>
                {t('salesPages.statementNew.printPreview')}
              </Button>
            </span>
          )}
          {canSend ? (
            <Button variant="outline" onClick={() => setSending(true)}>
              {t('salesPages.list.send')}
            </Button>
          ) : null}
          {kind === 'invoice' && isStaff ? (
            <Button variant="ghost" onClick={makeRecurring}>
              {t('purchasePages.form.makeRecurring')}
            </Button>
          ) : (
            <span title={t('ui.comingSoon')}>
              <Button variant="ghost" disabled>
                {t('purchasePages.form.makeRecurring')}
              </Button>
            </span>
          )}
          {doc ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="gap-1">
                  {t('purchasePages.form.more')} <ChevronDown className="size-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center">
                {kind === 'invoice' &&
                isStaff &&
                (doc.status === 'approved' || doc.status === 'partially_paid') ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/sales/receive-payment/new"
                      search={{ contactId: doc.contactId, invoiceId: doc.id }}
                    >
                      {t('salesPages.list.receivePayment')}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
                {canApply ? (
                  <DropdownMenuItem onSelect={() => setApplying(true)}>
                    {t('salesPages.documentForm.applyToInvoices')}
                  </DropdownMenuItem>
                ) : null}
                {kind === 'quote' && isStaff && doc.status !== 'converted'
                  ? (['pending', 'accepted', 'declined'] as const)
                      .filter((st) => st !== doc.status)
                      .map((st) => (
                        <DropdownMenuItem key={st} onSelect={() => quoteStatus.mutate(st)}>
                          {t(`salesPages.documentForm.markAs.${st}`)}
                        </DropdownMenuItem>
                      ))
                  : null}
                {posted && isStaff && !einvoiceLocked ? (
                  <DropdownMenuItem onSelect={() => setVoiding(true)}>
                    {t('purchasePages.form.void')}
                  </DropdownMenuItem>
                ) : null}
                {isOrder && isStaff && doc.status !== 'void' ? (
                  <DropdownMenuItem onSelect={() => closeOrder.mutate(doc.status === 'open')}>
                    {doc.status === 'open'
                      ? t(
                          `salesPages.documentForm.orderKinds.${kind as 'sales_order' | 'delivery_challan'}.close`,
                        )
                      : t('purchasePages.form.reopen')}
                  </DropdownMenuItem>
                ) : null}
                {kind === 'delivery_challan' && isStaff && doc.status !== 'void' && !fulfilled ? (
                  <DropdownMenuItem onSelect={() => setVoiding(true)}>
                    {t('salesPages.documentForm.cancelChallan')}
                  </DropdownMenuItem>
                ) : null}
                {(doc.status === 'draft' ||
                  (kind === 'quote' && doc.status !== 'converted') ||
                  (kind === 'sales_order' && !fulfilled)) &&
                isStaff ? (
                  <DropdownMenuItem
                    onSelect={() => {
                      if (window.confirm(t(`salesPages.documentForm.kinds.${kind}.deleteConfirm`)))
                        del.mutate();
                    }}
                  >
                    {kind === 'quote' || kind === 'sales_order'
                      ? t('purchasePages.form.delete')
                      : t('purchasePages.form.deleteDraft')}
                  </DropdownMenuItem>
                ) : null}
                {doc.journalEntryId ? (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem asChild>
                      <Link
                        to="/accounting/journal-entries/$entryId"
                        params={{ entryId: doc.journalEntryId }}
                      >
                        {t('purchasePages.form.transactionJournal')}
                      </Link>
                    </DropdownMenuItem>
                  </>
                ) : null}
                {doc.voidEntryId ? (
                  <DropdownMenuItem asChild>
                    <Link
                      to="/accounting/journal-entries/$entryId"
                      params={{ entryId: doc.voidEntryId }}
                    >
                      {t('purchasePages.form.voidEntry')}
                    </Link>
                  </DropdownMenuItem>
                ) : null}
              </DropdownMenuContent>
            </DropdownMenu>
          ) : null}
        </>
      }
      footerRight={
        readOnly ? null : clientDraft ? (
          <Button disabled={save.isPending} onClick={() => submit('draft')} title="Ctrl+Alt+S">
            {save.isPending
              ? t('purchasePages.form.sending')
              : t('purchasePages.form.submitForApproval')}
          </Button>
        ) : posted ? (
          <Button disabled={save.isPending} onClick={() => submit('save')} title="Ctrl+Alt+S">
            {save.isPending ? t('common.saving') : t('common.saveAndClose')}
          </Button>
        ) : UNPOSTED.includes(kind) ? (
          <>
            <Button
              variant="outline"
              disabled={save.isPending}
              onClick={() => submit('approve-new')}
              title="Ctrl+Alt+D"
            >
              {t('common.saveAndNew')}
            </Button>
            <Button
              disabled={save.isPending}
              onClick={() => submit('approve-close')}
              title="Ctrl+Alt+S"
            >
              {save.isPending ? t('common.saving') : t('common.saveAndClose')}
            </Button>
          </>
        ) : (
          <>
            <Button variant="outline" disabled={save.isPending} onClick={() => submit('draft')}>
              {t('purchasePages.form.saveDraft')}
            </Button>
            <div className="inline-flex">
              <Button
                className="rounded-e-none"
                disabled={save.isPending}
                onClick={() => submit('approve-close')}
                title="Ctrl+Alt+S"
              >
                {save.isPending ? t('common.saving') : t('purchasePages.form.approveAndClose')}
              </Button>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    className="rounded-s-none border-s border-primary-foreground/30 px-2"
                    aria-label={t('purchasePages.form.moreSaveOptions')}
                    disabled={save.isPending}
                  >
                    <ChevronDown className="size-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => submit('approve-send')}>
                    {t('salesPages.documentForm.approveAndSend')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => submit('approve-new')}>
                    {t('purchasePages.form.approveAndNew')}
                  </DropdownMenuItem>
                  <DropdownMenuItem onSelect={() => submit('draft')}>
                    {t('purchasePages.form.saveDraft')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </>
        )
      }
    >
      <div className="mx-auto flex max-w-[1400px] flex-col gap-5">
        {errors.length ? (
          <ErrorBanner>
            <ul className="list-disc ps-4">
              {errors.map((e) => (
                <li key={e}>{e}</li>
              ))}
            </ul>
          </ErrorBanner>
        ) : null}
        {doc && (kind === 'invoice' || kind === 'credit_note') && doc.status !== 'draft' ? (
          <>
            <EInvoicePanel documentId={doc.id} label={label} />
            {doc.status !== 'void' ? <IrnPanel documentId={doc.id} number={doc.number} /> : null}
          </>
        ) : null}
        {doc &&
        ((kind === 'invoice' && POSTED.includes(doc.status)) ||
          (kind === 'delivery_challan' && doc.status !== 'void')) ? (
          <EwayBillPanel documentId={doc.id} number={doc.number} />
        ) : null}
        {fromOrder && source && !doc ? (
          <Notice>
            {t(
              `salesPages.documentForm.orderKinds.${source.kind as 'sales_order' | 'delivery_challan'}.linesOpen`,
              { number: ltrIn(source.number ?? '') },
            )}
          </Notice>
        ) : null}
        {isVoid ? (
          <Notice tone="muted">
            {doc?.voidReason
              ? t(`salesPages.documentForm.kinds.${kind}.voidNoticeReason`, {
                  reason: isoIn(doc.voidReason),
                })
              : t(`salesPages.documentForm.kinds.${kind}.voidNotice`)}
          </Notice>
        ) : doc && isOrder && doc.status === 'closed' ? (
          <Notice tone="muted">
            {t(
              `salesPages.documentForm.orderKinds.${kind as 'sales_order' | 'delivery_challan'}.closedNotice`,
            )}
          </Notice>
        ) : doc && isOrder && fulfilled ? (
          <Notice tone="muted">
            {t(
              `salesPages.documentForm.orderKinds.${kind as 'sales_order' | 'delivery_challan'}.fulfilledNotice`,
            )}
          </Notice>
        ) : doc?.opening ? (
          <Notice tone="muted">{t('purchasePages.form.openingNotice')}</Notice>
        ) : gstLocked ? (
          <Notice tone="muted">
            {irnActive
              ? kind === 'credit_note'
                ? t('salesPages.documentForm.irnLockedCreditNote')
                : t('salesPages.documentForm.irnLockedInvoice')
              : kind === 'credit_note'
                ? t('salesPages.documentForm.ewbLockedCreditNote')
                : t('salesPages.documentForm.ewbLockedInvoice')}
          </Notice>
        ) : posted && !einvoiceLocked ? (
          <Notice>
            {doc?.approvedByName && doc.approvedAt
              ? t('purchasePages.form.approvedByOn', {
                  name: isoIn(doc.approvedByName),
                  date: ltrIn(formatDate(doc.approvedAt)),
                })
              : doc?.approvedByName
                ? t('purchasePages.form.approvedBy', { name: isoIn(doc.approvedByName) })
                : doc?.approvedAt
                  ? t('purchasePages.form.approvedOn', { date: ltrIn(formatDate(doc.approvedAt)) })
                  : t('purchasePages.form.approved')}{' '}
            {t(`salesPages.documentForm.kinds.${kind}.editPosted`)}
            {allocated > 0n
              ? ` ${t('salesPages.documentForm.alreadyPaid', { amount: ltrIn(`${currency} ${formatMinor(allocated, exp)}`) })}`
              : ''}
            {doc?.sentAt
              ? ` ${t('salesPages.documentForm.sentOn', { date: ltrIn(formatDate(doc.sentAt)) })}`
              : ''}
          </Notice>
        ) : doc?.status === 'converted' ? (
          <Notice tone="muted">{t('salesPages.documentForm.quoteConverted')}</Notice>
        ) : null}
        {!isStaff && !clientEdit && me.isSuccess ? (
          <Notice tone="muted">{t('salesPages.documentForm.staffOnly')}</Notice>
        ) : null}
        {clientDraft ? <Notice>{t('salesPages.documentForm.clientReview')}</Notice> : null}
        {doc && doc.status === 'draft' ? (
          <ApprovalStrip documentId={doc.id} kind={kind} status={doc.status} />
        ) : null}

        {/* ---- header ---- */}
        <div className="flex flex-wrap items-start gap-6">
          <div className="grid flex-1 grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-4">
            <Field
              label={t('settingsPages.data.project.customerRequired')}
              error={fe('contactId')}
              className="col-span-2"
            >
              {() => (
                <Combobox
                  ariaLabel={t('salesPages.paymentView.customer')}
                  items={contactItems}
                  value={s.contactId}
                  invalid={Boolean(fe('contactId'))}
                  placeholder={t('settingsPages.company.templateForm.kinds.invoice.chooseContact')}
                  onChange={readOnly ? () => undefined : chooseContact}
                  onAddNew={
                    readOnly || !isStaff
                      ? undefined
                      : (typed) =>
                          openNewDrawer({
                            kind: 'customer',
                            name: typed,
                            onSaved: (c) => {
                              void qc
                                .invalidateQueries({ queryKey: ['contacts'] })
                                .then(() => chooseContact(c.id));
                            },
                          })
                  }
                />
              )}
            </Field>
            <Field
              label={t('settingsPages.company.templateForm.customerEmail')}
              className="col-span-2"
            >
              {(id) => (
                <Input
                  id={id}
                  value={s.customerEmail}
                  disabled={readOnly}
                  onChange={(e) => set('customerEmail', e.target.value)}
                  placeholder={t('salesPages.documentForm.customerEmailPlaceholder')}
                />
              )}
            </Field>
          </div>
          <div className="ms-auto text-end">
            <div className="text-xs uppercase tracking-wide text-muted-foreground">
              {UNPOSTED.includes(kind)
                ? t('purchasePages.form.amount')
                : kind === 'sales_receipt'
                  ? t('salesPages.paymentView.amountReceived')
                  : kind === 'credit_note'
                    ? t('purchasePages.form.credit')
                    : t('purchasePages.form.balanceDue')}
            </div>
            <div className="tnum text-[32px] font-semibold leading-tight" data-testid="balance-due">
              {currency}{' '}
              {formatMinor(
                UNPOSTED.includes(kind) || kind === 'sales_receipt' ? totals.total : balanceDue,
                exp,
              )}
            </div>
            {foreign ? (
              <div className="tnum text-sm text-muted-foreground" data-testid="balance-due-home">
                {(() => {
                  const h = toHome(
                    UNPOSTED.includes(kind) || kind === 'sales_receipt' ? totals.total : balanceDue,
                  );
                  return h === null ? '' : `${home} ${formatMinor(h)}`;
                })()}
              </div>
            ) : null}
          </div>
        </div>

        <div className="grid grid-cols-[repeat(auto-fill,minmax(190px,1fr))] gap-4">
          <Field label={t('salesPages.contactDetail.field.billingAddress')} className="row-span-2">
            {() => (
              <div
                className="min-h-20 whitespace-pre-line rounded-md border bg-muted/30 px-3 py-2 text-sm text-muted-foreground"
                data-testid="billing-address"
              >
                {contact
                  ? formatAddress(contact.billingAddress) || t('salesPages.documentForm.noAddress')
                  : doc?.billingAddress
                    ? Object.values(doc.billingAddress).filter(Boolean).join('\n')
                    : '—'}
                {contact?.trn
                  ? `\n${t('purchasePages.form.trnLine', { trn: ltrIn(formatTrn(contact.trn)) })}`
                  : ''}
              </div>
            )}
          </Field>
          {kind === 'invoice' ? (
            <Field label={t('purchasePages.form.terms')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  disabled={readOnly}
                  value={s.termsDays === null ? '' : String(s.termsDays)}
                  onChange={(e) => {
                    const t = e.target.value === '' ? null : Number(e.target.value);
                    setS((p) => ({
                      ...p,
                      termsDays: t,
                      dueEdited: false,
                      dueDate: t === null ? p.dueDate : addDays(p.issueDate, t),
                    }));
                  }}
                >
                  <option value="">{t('purchasePages.form.chooseTerms')}</option>
                  {termsOptions.map((t) => (
                    <option key={t.days} value={t.days}>
                      {t.label}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          <Field label={`${kindDateLabel(kind)} *`} error={fe('issueDate')}>
            {(id) => (
              <DateInput
                id={id}
                value={s.issueDate}
                disabled={readOnly}
                onChange={(v) =>
                  setS((p) => ({
                    ...p,
                    issueDate: v,
                    dueDate:
                      kind === 'invoice' && !p.dueEdited && p.termsDays !== null
                        ? addDays(v, p.termsDays)
                        : p.dueDate,
                  }))
                }
              />
            )}
          </Field>
          {kind === 'delivery_challan' ? (
            <Field label={t('salesPages.documentForm.challanType')} error={fe('challanType')}>
              {(id) => (
                <NativeSelect
                  id={id}
                  disabled={readOnly}
                  value={s.challanType}
                  aria-invalid={Boolean(fe('challanType')) || undefined}
                  onChange={(e) => set('challanType', e.target.value as ChallanType | '')}
                >
                  <option value="">{t('salesPages.documentForm.challanTypePlaceholder')}</option>
                  {CHALLAN_TYPES.map(([v]) => (
                    <option key={v} value={v}>
                      {challanTypeLabel(v)}
                    </option>
                  ))}
                </NativeSelect>
              )}
            </Field>
          ) : null}
          {kind === 'invoice' || kind === 'quote' || kind === 'sales_order' ? (
            <Field
              label={
                kind === 'quote'
                  ? t('salesPages.documentForm.expirationDate')
                  : kind === 'sales_order'
                    ? t('salesPages.documentForm.expectedShipmentDate')
                    : t('purchasePages.form.dueDate')
              }
              error={fe('dueDate')}
            >
              {(id) => (
                <DateInput
                  id={id}
                  value={s.dueDate}
                  disabled={readOnly}
                  onChange={(v) => setS((p) => ({ ...p, dueDate: v, dueEdited: true }))}
                />
              )}
            </Field>
          ) : null}
          {foreign ? (
            <ExchangeRateField
              currency={currency}
              home={home}
              date={s.issueDate}
              value={s.fxRate}
              disabled={readOnly}
              error={fe('fxRate')}
              onChange={(v) => set('fxRate', v)}
            />
          ) : null}
          <Field label={kindNumberLabel(kind)}>
            {(id) => (
              <Input
                id={id}
                value={doc?.number ?? ''}
                placeholder={
                  UNPOSTED.includes(kind)
                    ? t('purchasePages.form.assignedWhenSaved')
                    : t('purchasePages.form.assignedOnApproval')
                }
                disabled
              />
            )}
          </Field>
          {!UNPOSTED.includes(kind) ? (
            <Field
              label={t('purchasePages.form.dateOfSupply')}
              hint={t('salesPages.documentForm.dateOfSupplyHint')}
            >
              {(id) => (
                <DateInput
                  id={id}
                  value={s.supplyDate}
                  disabled={readOnly}
                  onChange={(v) => set('supplyDate', v)}
                />
              )}
            </Field>
          ) : null}
          <Field
            label={t('settingsPages.company.templateForm.placeOfSupply')}
            error={fe('placeOfSupply')}
            hint={isIndia() ? t('salesPages.documentForm.placeOfSupplyHintIndia') : undefined}
          >
            {(id) =>
              isIndia() ? (
                <NativeSelect
                  id={id}
                  disabled={readOnly}
                  value={s.placeOfSupply}
                  aria-invalid={Boolean(fe('placeOfSupply')) || undefined}
                  onChange={(e) => set('placeOfSupply', e.target.value)}
                >
                  <option value="">{t('settingsPages.company.templateForm.fromCustomer')}</option>
                  {GST_STATES.map(([code, name]) => (
                    <option key={code} value={code}>
                      {code} · {name}
                    </option>
                  ))}
                  <option value="96">
                    96 · {t('settingsPages.company.templateForm.otherCountries')}
                  </option>
                </NativeSelect>
              ) : (
                <NativeSelect
                  id={id}
                  disabled={readOnly}
                  value={s.placeOfSupply}
                  aria-invalid={Boolean(fe('placeOfSupply')) || undefined}
                  onChange={(e) => set('placeOfSupply', e.target.value)}
                >
                  <option value="">{t('salesPages.documentForm.outsideUaeNotSet')}</option>
                  {EMIRATES.map((e) => (
                    <option key={e} value={e}>
                      {emirateLabel(e)}
                    </option>
                  ))}
                </NativeSelect>
              )
            }
          </Field>
          <Field
            label={
              kind === 'sales_receipt'
                ? t('salesPages.paymentView.referenceNo')
                : t('settingsPages.company.templateForm.kinds.invoice.reference')
            }
          >
            {(id) => (
              <Input
                id={id}
                value={s.reference}
                maxLength={100}
                disabled={readOnly}
                onChange={(e) => set('reference', e.target.value)}
              />
            )}
          </Field>
          {kind === 'sales_receipt' ? (
            <>
              <Field label={t('purchasePages.form.paymentMethod')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={readOnly}
                    value={s.paymentMethod}
                    onChange={(e) => set('paymentMethod', e.target.value)}
                  >
                    <option value="" />
                    {PAYMENT_METHODS.map((m) => (
                      <option key={m.value} value={m.value}>
                        {paymentMethodLabel(m.value)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
              <Field label={t('salesPages.documentForm.depositTo')} error={fe('depositAccountId')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={readOnly}
                    value={s.depositAccountId ?? ''}
                    aria-invalid={Boolean(fe('depositAccountId')) || undefined}
                    onChange={(e) => set('depositAccountId', e.target.value || null)}
                  >
                    <option value="">{t('purchasePages.form.chooseAccount')}</option>
                    {depositAccounts.map((a) => (
                      <option key={a.id} value={a.id}>
                        {a.code} {nameFor(lang, a.fullName, a.fullNameAr)}
                      </option>
                    ))}
                  </NativeSelect>
                )}
              </Field>
            </>
          ) : null}
          {kind === 'credit_note' ? (
            <>
              <Field label={t('salesPages.documentForm.appliesToInvoice')}>
                {(id) => (
                  <NativeSelect
                    id={id}
                    disabled={readOnly || !s.contactId}
                    value={s.relatedDocumentId ?? ''}
                    onChange={(e) => set('relatedDocumentId', e.target.value || null)}
                  >
                    <option value="">{t('common.none')}</option>
                    {(invoicesForCredit.data ?? [])
                      .filter((d) => POSTED.includes(d.status) || d.id === s.relatedDocumentId)
                      .map((d) => (
                        <option key={d.id} value={d.id}>
                          {d.number} · {formatDate(d.issueDate)} · {d.currency}{' '}
                          {formatAmount(d.total)}
                        </option>
                      ))}
                  </NativeSelect>
                )}
              </Field>
              <Field
                label={t('salesPages.documentForm.reasonForCredit')}
                error={fe('creditReason')}
                className="col-span-2"
              >
                {(id) => (
                  <Input
                    id={id}
                    value={s.creditReason}
                    maxLength={500}
                    disabled={readOnly}
                    placeholder={t('salesPages.documentForm.reasonForCreditPlaceholder')}
                    onChange={(e) => set('creditReason', e.target.value)}
                  />
                )}
              </Field>
            </>
          ) : null}
        </div>

        <TrackingHeaderFields
          value={s.tracking}
          onChange={(v) => set('tracking', v)}
          disabled={readOnly}
          customerId={s.contactId}
        />

        <div className="flex items-center gap-2 self-end text-sm">
          <label htmlFor="amounts-are" className="text-muted-foreground">
            {t('purchasePages.form.amountsAre')}
          </label>
          <NativeSelect
            id="amounts-are"
            className="w-[200px]"
            disabled={readOnly}
            value={s.amountsAre}
            onChange={(e) => set('amountsAre', e.target.value as AmountsAre)}
          >
            <option value="exclusive">
              {t('purchasePages.form.exclusiveOf', { tax: taxName })}
            </option>
            <option value="inclusive">
              {t('purchasePages.form.inclusiveOf', { tax: taxName })}
            </option>
            <option value="out_of_scope">
              {t('purchasePages.form.outOfScopeOf', { tax: taxName })}
            </option>
          </NativeSelect>
        </div>

        {/* ---- line grid ---- */}
        <div
          className="overflow-x-auto rounded-lg border bg-card"
          role="grid"
          aria-label={t('salesPages.documentForm.lines', { kind: label })}
        >
          <table className="w-full min-w-[1200px] table-fixed text-sm">
            <thead className="border-b bg-muted/40 text-start text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <tr>
                <th className="w-10 px-2 py-2">#</th>
                <th className="w-[20%] px-2 py-2">{t('purchasePages.form.productService')}</th>
                <th className="px-2 py-2">{t('purchasePages.form.description')}</th>
                {isIndia() ? <th className="w-[100px] px-2 py-2">HSN/SAC</th> : null}
                <th className="w-[80px] px-2 py-2 text-end">{t('purchasePages.form.qty')}</th>
                {doc && isOrder ? (
                  <th className="w-[80px] px-2 py-2 text-end">
                    {t('salesPages.documentForm.invoiced')}
                  </th>
                ) : null}
                {doc && kind === 'sales_order' && isIndia() ? (
                  <th className="w-[80px] px-2 py-2 text-end">
                    {t('salesPages.documentForm.delivered')}
                  </th>
                ) : null}
                <th className="w-[120px] px-2 py-2 text-end">{t('purchasePages.form.rate')}</th>
                <th className="w-[130px] px-2 py-2 text-end">
                  {t('purchasePages.form.amount')}
                  {foreign ? ` (${currency})` : ''}
                </th>
                <th className="w-[130px] px-2 py-2">{taxShort}</th>
                {tracking.classes === 'per_line' ? (
                  <th className="w-[150px] px-2 py-2">{t('purchasePages.form.class')}</th>
                ) : null}
                <th className="w-10 px-2 py-2" />
              </tr>
            </thead>
            <tbody>
              {s.rows.map((r, i) => {
                const amt = totals.perRow.get(r.key);
                const n = t('salesPages.documentForm.lineN', { n: i + 1 });
                return (
                  <tr key={r.key} className="border-b last:border-0" data-testid={`line-${i + 1}`}>
                    <td className="px-2 py-1.5 text-muted-foreground">{i + 1}</td>
                    <td className="px-1 py-1.5">
                      <Combobox
                        ariaLabel={t('purchasePages.form.productServiceLine', { line: n })}
                        items={itemItems}
                        value={r.itemId}
                        invalid={lineErr(i, 'itemId') || lineErr(i, 'accountId')}
                        onChange={readOnly ? () => undefined : (id) => chooseItem(r.key, id)}
                        onAddNew={
                          readOnly || !isStaff
                            ? undefined
                            : (typed) =>
                                openNewDrawer({
                                  kind: 'item',
                                  name: typed,
                                  onSaved: (it) => {
                                    void qc
                                      .invalidateQueries({ queryKey: ['items'] })
                                      .then(() => chooseItem(r.key, it.id));
                                  },
                                })
                        }
                      />
                    </td>
                    <td className="px-1 py-1.5">
                      <Input
                        aria-label={t('purchasePages.form.descriptionLine', { line: n })}
                        value={r.description}
                        disabled={readOnly}
                        maxLength={1000}
                        onChange={(e) => updateRow(r.key, { description: e.target.value })}
                      />
                    </td>
                    {isIndia() ? (
                      <td className="px-1 py-1.5">
                        <Input
                          aria-label={t('salesPages.documentForm.hsnSacLine', { line: n })}
                          className="tnum"
                          inputMode="numeric"
                          maxLength={8}
                          value={r.hsnSac}
                          disabled={readOnly}
                          aria-invalid={lineErr(i, 'hsnSac') || undefined}
                          onChange={(e) =>
                            updateRow(r.key, { hsnSac: e.target.value.replace(/\D/g, '') })
                          }
                        />
                      </td>
                    ) : null}
                    <td className="px-1 py-1.5">
                      <Input
                        aria-label={t('purchasePages.form.qtyLine', { line: n })}
                        className="tnum text-end"
                        inputMode="decimal"
                        value={r.quantity}
                        disabled={readOnly}
                        aria-invalid={lineErr(i, 'quantity') || undefined}
                        placeholder={used(r) ? '1' : ''}
                        onChange={(e) =>
                          updateRow(r.key, { quantity: e.target.value.replace(/[^\d.]/g, '') })
                        }
                      />
                    </td>
                    {doc && isOrder ? (
                      <td className="tnum px-2 py-1.5 text-end text-muted-foreground">
                        {doc.lines[i]?.invoicedQuantity ?? ''}
                      </td>
                    ) : null}
                    {doc && kind === 'sales_order' && isIndia() ? (
                      <td className="tnum px-2 py-1.5 text-end text-muted-foreground">
                        {doc.lines[i]?.deliveredQuantity ?? ''}
                      </td>
                    ) : null}
                    <td className="px-1 py-1.5">
                      <AmountInput
                        aria-label={t('purchasePages.form.rateLine', { line: n })}
                        exp={exp}
                        value={r.rate}
                        disabled={readOnly}
                        invalid={lineErr(i, 'unitPrice')}
                        onValueChange={(v) =>
                          updateRow(r.key, {
                            rate: v,
                            ...(v && !r.taxCodeId ? { taxCodeId: defaultCode(contact) } : {}),
                          })
                        }
                      />
                    </td>
                    <td className="px-1 py-1.5">
                      <AmountInput
                        aria-label={t('purchasePages.form.amountLine', { line: n })}
                        exp={exp}
                        value={amt !== undefined && amt !== 0n ? minorToPlain(amt, exp) : ''}
                        disabled={readOnly}
                        onValueChange={(v) => {
                          const target = toMinor(v, exp) ?? 0n;
                          if (s.amountsAre === 'inclusive' || !v) {
                            updateRow(r.key, {
                              rate: v
                                ? minorToPlain(rateFor(target, r.quantity || '1') ?? target, exp)
                                : '',
                            });
                            return;
                          }
                          const rate = rateFor(target, r.quantity || '1');
                          updateRow(r.key, {
                            rate: rate === null ? '' : minorToPlain(rate, exp),
                            ...(!r.taxCodeId ? { taxCodeId: defaultCode(contact) } : {}),
                          });
                        }}
                      />
                    </td>
                    <td className="px-1 py-1.5">
                      <select
                        aria-label={t('purchasePages.form.taxLine', { tax: taxShort, line: n })}
                        className={cn(
                          'h-9 w-full rounded-md border border-input bg-card px-2 text-sm',
                          lineErr(i, 'taxCodeId') && 'border-destructive',
                        )}
                        disabled={readOnly}
                        value={r.taxCodeId ?? ''}
                        onChange={(e) => updateRow(r.key, { taxCodeId: e.target.value || null })}
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !e.shiftKey && i === s.rows.length - 1)
                            setS((p) => ({ ...p, rows: [...p.rows, emptyRow()] }));
                        }}
                      >
                        <option value="" />
                        {salesCodes
                          .concat(
                            r.taxCodeId &&
                              !salesCodes.some((t) => t.id === r.taxCodeId) &&
                              codeById.get(r.taxCodeId)
                              ? [codeById.get(r.taxCodeId)!]
                              : [],
                          )
                          .map((t) => (
                            <option key={t.id} value={t.id} title={nameFor(lang, t.name, t.nameAr)}>
                              {t.code} {formatRate(t.rate)}
                            </option>
                          ))}
                      </select>
                    </td>
                    {tracking.classes === 'per_line' ? (
                      <td className="px-1 py-1.5">
                        <ClassSelect
                          ariaLabel={t('purchasePages.form.classLine', { line: n })}
                          disabled={readOnly}
                          value={r.classId}
                          onChange={(classId) => updateRow(r.key, { classId })}
                        />
                      </td>
                    ) : null}
                    <td className="px-1 py-1.5 text-center">
                      {!readOnly ? (
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          tabIndex={-1}
                          aria-label={t('purchasePages.form.deleteLine', { line: n })}
                          onClick={() =>
                            setS((p) => {
                              const next = p.rows.filter((x) => x.key !== r.key);
                              return { ...p, rows: next.length ? next : [emptyRow()] };
                            })
                          }
                        >
                          <Trash2 className="size-4" />
                        </Button>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {!readOnly ? (
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setS((p) => ({ ...p, rows: [...p.rows, emptyRow(), emptyRow()] }))}
            >
              {t('purchasePages.form.addLines')}
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setS((p) => ({ ...p, rows: [emptyRow(), emptyRow()] }))}
            >
              {t('purchasePages.form.clearAllLines')}
            </Button>
          </div>
        ) : null}

        {/* ---- memo + totals ---- */}
        <div className="flex flex-wrap items-start justify-between gap-8">
          <div className="flex min-w-[320px] flex-1 flex-col gap-4">
            <Field
              label={t(`salesPages.documentForm.kinds.${kind}.messageOn`)}
              className="max-w-xl"
            >
              {(id) => (
                <Textarea
                  id={id}
                  value={s.memo}
                  maxLength={2000}
                  disabled={readOnly}
                  onChange={(e) => set('memo', e.target.value)}
                />
              )}
            </Field>
            {kind === 'invoice' ? (
              <Field label={t('salesPages.documentForm.messageOnStatement')} className="max-w-xl">
                {(id) => (
                  <Textarea
                    id={id}
                    value={s.statementMemo}
                    maxLength={1000}
                    disabled={readOnly}
                    onChange={(e) => set('statementMemo', e.target.value)}
                  />
                )}
              </Field>
            ) : null}
            {doc ? (
              <div className="max-w-xl">
                <AttachmentsPanel
                  entityType="document"
                  entityId={doc.id}
                  canChange={isStaff || clientEdit}
                />
              </div>
            ) : null}
            {doc && doc.allocations.length ? (
              <div
                className="max-w-xl rounded-lg border bg-card p-3 text-sm"
                data-testid="allocations"
              >
                <div className="mb-2 font-medium">
                  {kind === 'credit_note'
                    ? t('purchasePages.form.appliedTo')
                    : t('purchasePages.form.paymentsAndCredits')}
                </div>
                <ul className="divide-y">
                  {doc.allocations.map((a) => (
                    <li key={a.id} className="flex justify-between py-1">
                      <Link
                        className="text-primary hover:underline"
                        to={
                          a.type === 'payment'
                            ? `/sales/payments/${a.refId}`
                            : a.type === 'credit_note'
                              ? `/sales/credit-notes/${a.refId}`
                              : `/sales/invoices/${a.refId}`
                        }
                      >
                        {kindLabel(
                          a.type === 'payment'
                            ? 'payment'
                            : a.type === 'credit_note'
                              ? 'credit_note'
                              : 'invoice',
                        )}{' '}
                        {ltrIn(a.refNumber ?? '')}
                      </Link>
                      <span className="tnum">
                        {formatDate(a.date)} · {currency} {formatAmount(a.amount)}
                        {a.exchangeGainLoss ? (
                          <span className="block text-xs text-muted-foreground">
                            {t(
                              a.exchangeGainLoss.startsWith('-')
                                ? 'purchasePages.form.exchangeLoss'
                                : 'purchasePages.form.exchangeGain',
                              {
                                amount: `${home} ${formatAmount(a.exchangeGainLoss.replace('-', ''))}`,
                              },
                            )}
                          </span>
                        ) : null}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
            {doc?.sources?.length || doc?.fulfilledBy?.length ? (
              <div
                className="max-w-xl text-sm text-muted-foreground"
                data-testid="linked-documents"
              >
                {doc.sources?.length ? (
                  <p>
                    {t('salesPages.documentForm.from')}{' '}
                    {doc.sources.map((d, i) => (
                      <span key={d.id}>
                        {i ? ', ' : ''}
                        <Link
                          className="text-primary hover:underline"
                          to={`${KIND_INFO[d.kind].path}/${d.id}`}
                        >
                          {kindLabel(d.kind).toLowerCase()} {ltrIn(d.number ?? '')}
                        </Link>
                      </span>
                    ))}
                  </p>
                ) : null}
                {doc.fulfilledBy?.length ? (
                  <p>
                    {kind === 'sales_order'
                      ? t('salesPages.documentForm.invoicesAndChallans')
                      : t('salesPages.documentForm.invoices')}
                    :{' '}
                    {doc.fulfilledBy.map((d, i) => (
                      <span key={d.id}>
                        {i ? ', ' : ''}
                        <Link
                          className="text-primary hover:underline"
                          to={`${KIND_INFO[d.kind].path}/${d.id}`}
                        >
                          {d.number ? ltrIn(d.number) : t('salesPages.list.draftLower')}
                        </Link>
                        {d.status === 'void'
                          ? ` ${t('salesPages.documentForm.voidSuffix')}`
                          : d.status === 'draft'
                            ? ` ${t('salesPages.documentForm.draftSuffix')}`
                            : ''}
                      </span>
                    ))}
                  </p>
                ) : null}
              </div>
            ) : null}
            {doc?.relatedDocumentId && doc.relatedDocumentKind ? (
              <p className="text-sm text-muted-foreground">
                {kind === 'invoice' || kind === 'sales_order'
                  ? t('salesPages.documentForm.convertedFromQuote')
                  : t('salesPages.documentForm.originalInvoice')}{' '}
                <Link
                  className="text-primary hover:underline"
                  to={`${KIND_INFO[doc.relatedDocumentKind as SalesKind].path}/${doc.relatedDocumentId}`}
                >
                  {ltrIn(doc.relatedDocumentNumber ?? '')}
                </Link>
              </p>
            ) : null}
          </div>
          <dl
            className="grid w-[360px] grid-cols-[1fr_auto] gap-x-6 gap-y-2 text-sm"
            data-testid="totals"
          >
            <dt className="text-muted-foreground">{t('purchasePages.form.subtotal')}</dt>
            <dd className="tnum text-end" data-testid="subtotal">
              {formatMinor(totals.net, exp)}
            </dd>
            {totals.byCode.map((b) => (
              <Row2 key={b.label} label={b.label} value={formatMinor(b.tax, exp)} />
            ))}
            {totals.byCode.length === 0 ? (
              <Row2 label={taxName} value={formatMinor(0n, exp)} />
            ) : null}
            <dt className="border-t pt-2 font-semibold">{t('purchasePages.form.total')}</dt>
            <dd className="tnum border-t pt-2 text-end font-semibold" data-testid="total">
              {currency} {formatMinor(totals.total, exp)}
            </dd>
            {kind === 'invoice' || kind === 'credit_note' ? (
              <>
                {allocated > 0n ? (
                  <Row2
                    label={
                      kind === 'credit_note'
                        ? t('purchasePages.form.applied')
                        : t('purchasePages.form.paymentsAndCredits')
                    }
                    value={`-${formatMinor(allocated, exp)}`}
                  />
                ) : null}
                <dt className="font-semibold">
                  {kind === 'credit_note'
                    ? t('purchasePages.form.creditRemaining')
                    : t('purchasePages.form.balanceDue')}
                </dt>
                <dd className="tnum text-end font-semibold">
                  {currency} {formatMinor(balanceDue, exp)}
                </dd>
              </>
            ) : null}
            {kind === 'sales_receipt' ? (
              <Row2
                label={t('salesPages.paymentView.amountReceived')}
                value={`${currency} ${formatMinor(totals.total, exp)}`}
              />
            ) : null}
            <HomeTotals
              currency={currency}
              home={home}
              rate={fxRate}
              rows={[
                // VAT in AED as the ledger posts it: per VAT code at the rate (FTA)
                {
                  label: t('purchasePages.form.vat'),
                  minor: totals.byCode.reduce((a, b) => a + (toHome(b.tax) ?? 0n), 0n),
                  testId: 'home-vat',
                },
                {
                  label: t('purchasePages.form.total'),
                  minor: toHome(totals.total),
                  testId: 'home-total',
                },
              ]}
            />
          </dl>
        </div>
        <p className="text-xs text-muted-foreground">
          {posted
            ? t('salesPages.documentForm.shortcutsPosted')
            : !UNPOSTED.includes(kind)
              ? t('salesPages.documentForm.shortcutsApprove')
              : doc
                ? t('salesPages.documentForm.shortcutsSaved')
                : t('salesPages.documentForm.shortcutsNew')}
        </p>
        {doc ? <DocumentActivity id={doc.id} /> : null}
      </div>
      {doc && canSend ? (
        <SendDialog doc={doc} open={sending} onClose={() => setSending(false)} />
      ) : null}
      {doc && kind === 'credit_note' ? (
        <ApplyCreditDialog
          credit={doc}
          open={applying && canApply}
          onClose={() => setApplying(false)}
        />
      ) : null}
      {doc ? (
        <VoidDialog
          open={voiding}
          title={t(`salesPages.documentForm.kinds.${kind}.voidTitle`, {
            number: ltrIn(doc.number ?? ''),
          })}
          description={
            kind === 'delivery_challan'
              ? t('salesPages.documentForm.voidChallanDescription')
              : t('purchasePages.form.voidDescription')
          }
          minDate={doc.issueDate}
          pending={voidM.isPending}
          error={voidM.error ? capital(voidM.error.message) : null}
          onCancel={() => setVoiding(false)}
          onConfirm={(date, reason) => voidM.mutate({ date, reason })}
        />
      ) : null}
    </OverlayPage>
  );
}

function Row2({ label, value }: { label: string; value: string }) {
  return (
    <>
      <dt className="text-muted-foreground">{label}</dt>
      <dd className="tnum text-end">{value}</dd>
    </>
  );
}

function Notice({ children, tone = 'info' }: { children: ReactNode; tone?: 'info' | 'muted' }) {
  return (
    <div
      className={cn(
        'rounded-md border px-3 py-2 text-sm',
        tone === 'info'
          ? 'border-info/30 bg-info/5 text-foreground'
          : 'border-border bg-muted text-muted-foreground',
      )}
      data-testid="form-notice"
    >
      {children}
    </div>
  );
}

/** A user's text inside an Arabic sentence keeps its own direction (like <bdi>); English is unchanged. */
const isoIn = (s: string): string => (i18n.language === 'ar' ? `\u2068${s}\u2069` : s);

const capital = (m: string) => (m ? m.charAt(0).toUpperCase() + m.slice(1) : m);
